import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Collapse,
  TablePagination,
  useTheme,
  Tabs,
  Tab
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { JewelerReportData } from '../utils/types';
import { formatMoney } from '../utils/global';
import ProductCategoryAnalysis from './ProductCategoryAnalysis';
import EmployeePerformanceAnalysis from './EmployeePerformanceAnalysis';

type JewelerReportTableProps = {
  data: JewelerReportData[];
  onJewelerSelect?: (jewelerId: string) => void;
  showBranches?: boolean;
};

export default function JewelerReportTable({ 
  data, 
  onJewelerSelect,
  showBranches = true 
}: JewelerReportTableProps) {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<{ [key: string]: number }>({});

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleRowExpansion = (jewelerId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(jewelerId)) {
      newExpanded.delete(jewelerId);
    } else {
      newExpanded.add(jewelerId);
    }
    setExpandedRows(newExpanded);
  };

  const handleTabChange = (jewelerId: string, newValue: number) => {
    setActiveTab(prev => ({
      ...prev,
      [jewelerId]: newValue
    }));
  };

  const getProfitLossColor = (profit: number, loss: number) => {
    const net = profit - loss;
    if (net > 0) return theme.palette.success.main;
    if (net < 0) return theme.palette.error.main;
    return theme.palette.grey[500];
  };

  const getProfitLossChip = (profit: number, loss: number) => {
    const net = profit - loss;
    const color = getProfitLossColor(profit, loss);
    
    return (
      <Chip
        label={`${net >= 0 ? '+' : ''}${formatMoney(net.toString())} TL`}
        sx={{
          backgroundColor: color,
          color: 'white',
          fontWeight: 600,
          fontSize: '0.75rem'
        }}
        size="small"
      />
    );
  };

  const paginatedData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
              <TableCell sx={{ fontWeight: 600 }}>Kuyumcu</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Toplam Ciro</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Toplam Kar</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Net Sonuç</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>İşlem Sayısı</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Ort. İşlem</TableCell>
              {showBranches && (
                <TableCell align="center" sx={{ fontWeight: 600 }}>Mağazalar</TableCell>
              )}
              <TableCell align="center" sx={{ fontWeight: 600 }}>Detay</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((jeweler) => (
              <React.Fragment key={jeweler.jewelerId}>
                <TableRow 
                  hover
                  sx={{ 
                    cursor: onJewelerSelect ? 'pointer' : 'default',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover
                    }
                  }}
                  onClick={() => onJewelerSelect?.(jeweler.jewelerId)}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {jeweler.companyName || jeweler.jewelerName}
                      </Typography>
                      {jeweler.companyName && (
                        <Typography variant="body2" color="text.secondary">
                          {jeweler.jewelerName}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatMoney(jeweler.totalRevenue.toString())} TL
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                      {formatMoney(jeweler.totalProfit.toString())} TL
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
                      {formatMoney(jeweler.totalLoss.toString())} TL
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {getProfitLossChip(jeweler.totalProfit, jeweler.totalLoss)}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {jeweler.transactionCount}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {formatMoney(jeweler.averageTransactionValue.toString())} TL
                    </Typography>
                  </TableCell>
                  {showBranches && (
                    <TableCell align="center">
                      <Typography variant="body2">
                        {jeweler.branches.length} mağaza
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleRowExpansion(jeweler.jewelerId);
                      }}
                    >
                      {expandedRows.has(jeweler.jewelerId) ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </TableCell>
                </TableRow>
                
                {/* Expanded Row - Monthly Breakdown */}
                <TableRow>
                  <TableCell colSpan={showBranches ? 9 : 8} sx={{ py: 0 }}>
                    <Collapse in={expandedRows.has(jeweler.jewelerId)} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2, backgroundColor: theme.palette.grey[50] }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                          Aylık Detaylar
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Ay</TableCell>
                              <TableCell align="right">Ciro</TableCell>
                              <TableCell align="right">Kar</TableCell>
                              <TableCell align="right">Zarar</TableCell>
                              <TableCell align="right">Net</TableCell>
                              <TableCell align="right">İşlem</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {jeweler.monthlyBreakdown.map((month) => (
                              <TableRow key={`${month.year}-${month.month}`}>
                                <TableCell>{month.monthName} {month.year}</TableCell>
                                <TableCell align="right">
                                  {formatMoney(month.revenue.toString())} TL
                                </TableCell>
                                <TableCell align="right" sx={{ color: 'success.main' }}>
                                  {formatMoney(month.profit.toString())} TL
                                </TableCell>
                                <TableCell align="right" sx={{ color: 'error.main' }}>
                                  {formatMoney(month.loss.toString())} TL
                                </TableCell>
                                <TableCell align="right">
                                  {getProfitLossChip(month.profit, month.loss)}
                                </TableCell>
                                <TableCell align="right">
                                  {month.transactionCount}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>

                        {/* Branch Details */}
                        {showBranches && (
                          <>
                            <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 600 }}>
                              Mağaza Detayları
                            </Typography>
                            
                            {/* Tabs for different analysis */}
                            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                              <Tabs 
                                value={activeTab[jeweler.jewelerId] || 0} 
                                onChange={(e, newValue) => handleTabChange(jeweler.jewelerId, newValue)}
                                aria-label="mağaza detayları tabs"
                              >
                                <Tab label="Mağaza Listesi" />
                                <Tab label="Ürün Kategorisi Analizi" />
                                <Tab label="Personel Performansı" />
                              </Tabs>
                            </Box>

                            {/* Tab Content */}
                            {activeTab[jeweler.jewelerId] === 0 && (
                              jeweler.branches.length > 0 ? (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Mağaza</TableCell>
                                      <TableCell align="right">Ciro</TableCell>
                                      <TableCell align="right">Kar</TableCell>
                                      <TableCell align="right">Net</TableCell>
                                      <TableCell align="right">İşlem</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {jeweler.branches.map((branch) => (
                                      <TableRow key={branch.branchId}>
                                        <TableCell>{branch.branchName}</TableCell>
                                        <TableCell align="right">
                                          {formatMoney(branch.revenue.toString())} TL
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: 'success.main' }}>
                                          {formatMoney(branch.profit.toString())} TL
                                        </TableCell>
                                        <TableCell align="right">
                                          {getProfitLossChip(branch.profit, branch.loss)}
                                        </TableCell>
                                        <TableCell align="right">
                                          {branch.transactionCount}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              ) : (
                                <Box sx={{ p: 2, background: '#F8F9FA', borderRadius: 1 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Bu kuyumcuya ait mağaza bulunmuyor.
                                  </Typography>
                                </Box>
                              )
                            )}

                            {activeTab[jeweler.jewelerId] === 1 && (
                              <ProductCategoryAnalysis 
                                data={[
                                  {
                                    category: "Altın Yüzük",
                                    totalRevenue: 5000000,
                                    totalProfit: 800000,
                                    totalQuantity: 25,
                                    transactionCount: 20,
                                    averagePrice: 200000,
                                    profitMargin: 16.0
                                  },
                                  {
                                    category: "Gümüş Kolye",
                                    totalRevenue: 3000000,
                                    totalProfit: 450000,
                                    totalQuantity: 15,
                                    transactionCount: 12,
                                    averagePrice: 200000,
                                    profitMargin: 15.0
                                  },
                                  {
                                    category: "Elmas Küpe",
                                    totalRevenue: 8000000,
                                    totalProfit: 1200000,
                                    totalQuantity: 8,
                                    transactionCount: 6,
                                    averagePrice: 1000000,
                                    profitMargin: 15.0
                                  }
                                ]}
                                chartType="bar"
                              />
                            )}

                            {activeTab[jeweler.jewelerId] === 2 && (
                              <EmployeePerformanceAnalysis 
                                data={[
                                  {
                                    employeeId: "emp1",
                                    firstName: "Ahmet",
                                    lastName: "Yılmaz",
                                    email: "ahmet@example.com",
                                    totalRevenue: 4000000,
                                    totalProfit: 600000,
                                    totalTransactions: 25,
                                    averageTransactionValue: 160000,
                                    profitMargin: 15.0,
                                    monthlyPerformance: []
                                  },
                                  {
                                    employeeId: "emp2",
                                    firstName: "Fatma",
                                    lastName: "Demir",
                                    email: "fatma@example.com",
                                    totalRevenue: 3500000,
                                    totalProfit: 525000,
                                    totalTransactions: 20,
                                    averageTransactionValue: 175000,
                                    profitMargin: 15.0,
                                    monthlyPerformance: []
                                  },
                                  {
                                    employeeId: "emp3",
                                    firstName: "Mehmet",
                                    lastName: "Kaya",
                                    email: "mehmet@example.com",
                                    totalRevenue: 3000000,
                                    totalProfit: 450000,
                                    totalTransactions: 18,
                                    averageTransactionValue: 166667,
                                    profitMargin: 15.0,
                                    monthlyPerformance: []
                                  }
                                ]}
                                chartType="bar"
                              />
                            )}
                          </>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Sayfa başına satır:"
        labelDisplayedRows={({ from, to, count }) => 
          `${from}-${to} / ${count !== -1 ? count : `more than ${to}`}`
        }
      />
    </Paper>
  );
}
